import { createAsyncThunk, createEntityAdapter, createSlice, current, EntityState, nanoid, } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { User } from "../user/user";
import { TalentStatus } from "../talents/types/common";
import { EventPayload } from "../../common/types/Event";
import { CandidateRejectionReason } from "../../common/types/CandidateRejectionReason";
import {
  approveIntroducedTalent,
  AskWorkInterest,
  askWorkInterestRequest,
  CancelCandidateRejection,
  CancelCandidateWithdrawal,
  CancelTalentIntroductions,
  cancelCandidateRejection,
  cancelCandidateWithdrawal,
  cancelTalentIntroductions,
  Candidate,
  CandidateApprovedByCustomer,
  CandidateChosenForInterview,
  CandidateInterestAsked,
  CandidateIntroducedToCustomer,
  CandidateIntroductionsCancelled,
  CandidatePicked,
  CandidateRejectionMessageSent,
  CandidateRejected,
  CandidateRejectionCancelled,
  CandidateWithdrew,
  CandidateWithdrawalCancelled,
  ChooseCandidatesForInterview,
  chooseCandidatesForInterview,
  FetchCandidatesRequest,
  FetchCandidatesResult,
  IntroduceTalentsToCustomer,
  introduceTalentsToCustomer,
  LongOrderManualTalentSearchFilters,
  longOrderManualTalentSearchQuery,
  LongOrderManualTalentSearchResult,
  PickCandidate,
  pickCandidate,
  PickCandidateRedux,
  PickedCandidate,
  pickedCandidates,
  recommendedCandidates,
  rejectCandidate,
  RejectCandidate,
  SendCandidateRejectedMessagePayload,
  sendCandidateRejectedMessage,
  WithdrawCandidate,
  withdrawCandidate,
} from "./TalentsStatusAPI";
import { getJobAdApplicants, JobAdApplicant, } from "../jobAds/jodAdApplicants/jobAdApplicantsAPI";
import { CandidateStatus } from "./editOrder/constants";

const talentsStatusAdapter = createEntityAdapter<
  PickedCandidate,
  PickedCandidate["talentId"]
>({
  selectId: (candidate) => candidate.talentId,
});

const talentStatusJobAdApplicantsAdapter = createEntityAdapter<
  JobAdApplicant,
  JobAdApplicant["id"]
>({
  selectId: (jobAdApplicant) => jobAdApplicant.id,
});

const selectPickedById = (state: SliceState, id: string) =>
  state.picked.entities[id];

const selectRecommendedById = (state: SliceState, id: string) =>
  state.recommended.entities[id];

const selectJobAdApplicantById = (state: SliceState, id: string) =>
  state.jobAdApplicants.entities[id];

type SliceState = {
  picked: EntityState<Candidate, Candidate["talentId"]>;
  recommended: EntityState<Candidate, Candidate["talentId"]>;
  jobAdApplicants: EntityState<JobAdApplicant, JobAdApplicant["id"]>;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

type WithCurrentUser<T> = { data: T; currentUser: User | null };

const resolveCurrentUser = (getState: () => RootState): User | null =>
  getState().user.user;

export const pickCandidates = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidatePicked>[]>,
  PickCandidateRedux | PickCandidate,
  { state: RootState }
>(
  "talentsStatus/pickCandidates",
  async (
    body,
    { requestId, getState },
  ): Promise<WithCurrentUser<EventPayload<CandidatePicked>[]>> => {
    const currentUser = resolveCurrentUser(getState);
    if ("talents" in body) {
      // other talents from LongOrderManualTalentSearch query
      const talentIds = body.talents.map((talent) => talent.talentId);
      const newPayload = {
        bundleId: body.bundleId,
        talentIds,
      };
      return {
        data: await pickCandidate(newPayload, { requestId }),
        currentUser,
      };
    } else {
      // recommended talents from recommendedCandidates query
      return { data: await pickCandidate(body, { requestId }), currentUser };
    }
  },
);

export const rejectCandidates = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateRejected>[]>,
  RejectCandidate,
  { state: RootState }
>(
  "talentsStatus/rejectCandidate",
  async (payload: RejectCandidate, { requestId, getState }) => ({
    data: await rejectCandidate(payload, { requestId }),
    currentUser: resolveCurrentUser(getState),
  }),
);

export const cancelTalentRejection = createAsyncThunk<
  EventPayload<CandidateRejectionCancelled>[],
  CancelCandidateRejection,
  { state: RootState }
>(
  "talentsStatus/cancelCandidateRejection",
  async (payload: CancelCandidateRejection, { requestId }) =>
    await cancelCandidateRejection(payload, { requestId }),
);

export const sendRejectedMessageToCandidates = createAsyncThunk<
  EventPayload<CandidateRejectionMessageSent>[],
  SendCandidateRejectedMessagePayload,
  { state: RootState }
>(
  "talentsStatus/sendRejectedMessageToCandidates",
  async (payload: SendCandidateRejectedMessagePayload, { requestId }) =>
    await sendCandidateRejectedMessage(payload, { requestId }),
);

export const askWorkInterest = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateInterestAsked>[]>,
  AskWorkInterest,
  { state: RootState }
>(
  "talentsStatus/askWorkInterest",
  async (payload: AskWorkInterest, { requestId, getState }) => ({
    data: await askWorkInterestRequest(payload, { requestId }),
    currentUser: resolveCurrentUser(getState),
  }),
);

export const introduceToCustomer = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateIntroducedToCustomer>[]>,
  IntroduceTalentsToCustomer,
  { state: RootState }
>(
  "talentsStatus/chooseCandidatesForIntroduction",
  async (
    body,
    { requestId, getState },
  ): Promise<
    WithCurrentUser<EventPayload<CandidateIntroducedToCustomer>[]>
  > => ({
    data: await introduceTalentsToCustomer(body, { requestId }),
    currentUser: resolveCurrentUser(getState),
  }),
);

export const chooseForInterview = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateChosenForInterview>[]>,
  ChooseCandidatesForInterview,
  { state: RootState }
>(
  "talentsStatus/chooseCandidatesForInterview",
  async (
    body,
    { requestId, getState },
  ): Promise<WithCurrentUser<EventPayload<CandidateChosenForInterview>[]>> => ({
    data: await chooseCandidatesForInterview(body, { requestId }),
    currentUser: resolveCurrentUser(getState),
  }),
);

export const approveIntroducedTalents = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateApprovedByCustomer>[]>,
  {
    bundleId: string;
    talentIds: string[];
    approved: boolean;
    rejectionReason?: CandidateRejectionReason | null;
    rejectionComment?: string | null;
  },
  { state: RootState }
>(
  "talentsStatus/approveIntroducedTalent",
  async ({ talentIds, ...details }, { requestId, getState }) => {
    const currentUser = resolveCurrentUser(getState);

    const events = await Promise.all(
      talentIds.map((talentId) =>
        approveIntroducedTalent({ ...details, talentId }, { requestId }),
      ),
    );

    return {
      data: events.flat(),
      currentUser,
    };
  },
);

export const withdrawTalent = createAsyncThunk<
  WithCurrentUser<EventPayload<CandidateWithdrew>[]>,
  WithdrawCandidate,
  { state: RootState }
>(
  "talentsStatus/withdrawCandidate",
  async (
    body,
    { requestId, getState },
  ): Promise<WithCurrentUser<EventPayload<CandidateWithdrew>[]>> => ({
    data: await withdrawCandidate(body, { requestId }),
    currentUser: resolveCurrentUser(getState),
  }),
);

export const cancelTalentWithdrawal = createAsyncThunk<
  EventPayload<CandidateWithdrawalCancelled>[],
  CancelCandidateWithdrawal,
  { state: RootState }
>(
  "talentsStatus/cancelCandidateWithdrawal",
  async (body, { requestId }) =>
    await cancelCandidateWithdrawal(body, { requestId }),
);

export const cancelIntroduceToCustomer = createAsyncThunk<
  EventPayload<CandidateIntroductionsCancelled>[],
  CancelTalentIntroductions,
  { state: RootState }
>(
  "talentsStatus/cancelCandidatesForIntroduction",
  async (
    body,
    { requestId },
  ): Promise<EventPayload<CandidateIntroductionsCancelled>[]> => {
    return await cancelTalentIntroductions(body, { requestId });
  },
);

export const filterRecommendedCandidates = (
  recommendedCandidates: Candidate[],
  pickedCandidates: PickedCandidate[],
  jobAdApplicants: JobAdApplicant[],
): Candidate[] => {
  const pickedTalentIds = new Set(
    pickedCandidates.map((candidate) => candidate.talentId),
  );
  const applicantTalentIds = new Set(
    jobAdApplicants.map((applicant) => applicant.id),
  );

  return recommendedCandidates.filter(
    (candidate) =>
      !pickedTalentIds.has(candidate.talentId) &&
      !applicantTalentIds.has(candidate.talentId),
  );
};

export const enrichCandidatesWithJobAdInfo = <T extends Candidate>(
  candidates: T[],
  jobAdApplicants: JobAdApplicant[],
): T[] => {
  const applicantsByTalentId = new Map(
    jobAdApplicants.map((applicant) => [applicant.id, applicant]),
  );

  return candidates.map((candidate) => {
    const applicant = applicantsByTalentId.get(candidate.talentId);
    if (!applicant) {
      return candidate;
    }

    return {
      ...candidate,
      applieadAt: applicant.appliedAt,
      hasCoverLetter: applicant.hasCoverLetter,
      exclusivityEntryIds: applicant.exclusivityEntryIds,
    };
  });
};

export const enrichPickedCandidatesWithMatchingInfo = (
  pickedCandidates: PickedCandidate[],
  matchingCandidates: Candidate[],
): PickedCandidate[] => {
  const matchingByTalentId = new Map(
    matchingCandidates.map((candidate) => [candidate.talentId, candidate]),
  );

  return pickedCandidates.map((candidate) => {
    const matchingCandidate = matchingByTalentId.get(candidate.talentId);
    if (!matchingCandidate) {
      return candidate;
    }

    return {
      ...candidate,
      matchingDetails:
        matchingCandidate.matchingDetails ?? candidate.matchingDetails,
      llt: matchingCandidate.llt ?? candidate.llt,
    };
  });
};

export const longOrderNewTalentStatuses = [
  TalentStatus.INCOMPLETE,
  TalentStatus.NEW,
  TalentStatus.INTERVIEW_PENDING,
  TalentStatus.INTERVIEW,
];

export const isLongOrderNewTalentStatus = (status: TalentStatus) =>
  longOrderNewTalentStatuses.includes(status);

export const fetchCandidates = createAsyncThunk<
  FetchCandidatesResult,
  FetchCandidatesRequest,
  { state: RootState }
>("talentsStatus/fetchCandidates", async (request, { requestId }) => {
  // TODO: this could be wrapped into single query in the backend (see YLPE-3740)
  const size = 2000;
  const [picked, allRecommendedActive, allRecommendedNew, allApplicants] =
    await Promise.all([
    request.picked
      ? pickedCandidates(request.bundleId, { requestId })
      : { candidates: [] },
    request.recommended
      ? recommendedCandidates(
          {
            bundleId: request.bundleId,
            size,
            talentStatus: [TalentStatus.ACTIVE],
            details: true,
          },
          { requestId },
        )
      : { candidates: [] },
    request.recommended
      ? recommendedCandidates(
          {
            bundleId: request.bundleId,
            size,
            talentStatus: longOrderNewTalentStatuses,
            details: true,
          },
          { requestId },
        )
      : { candidates: [] },
    request.applicants
      ? getJobAdApplicants(
          { bundleId: request.bundleId, pageSize: size.toString() },
          { requestId },
        )
      : { rows: [] },
    ]);

  const allRecommended = [
    ...allRecommendedActive.candidates,
    ...allRecommendedNew.candidates,
  ];
  const recommendedWithJobAdInfo = enrichCandidatesWithJobAdInfo(
    allRecommended,
    allApplicants.rows,
  );
  const pickedCandidatesWithMatchingInfo =
    enrichPickedCandidatesWithMatchingInfo(
      enrichCandidatesWithJobAdInfo(picked.candidates, allApplicants.rows),
      recommendedWithJobAdInfo,
    );
  const recommended = filterRecommendedCandidates(
    recommendedWithJobAdInfo,
    pickedCandidatesWithMatchingInfo,
    allApplicants.rows,
  );

  const applicants = allApplicants.rows.filter(
    (applicant) =>
      !pickedCandidatesWithMatchingInfo.some(
        (pickedCandidate) => pickedCandidate.talentId === applicant.id,
      ),
  );

  return {
    picked: pickedCandidatesWithMatchingInfo,
    recommended,
    applicants,
  };
});

export const searchOtherTalents = createAsyncThunk<
  LongOrderManualTalentSearchResult[],
  LongOrderManualTalentSearchFilters,
  { state: RootState }
>(
  "talentsStatus/searchOtherTalents",
  async (filters: LongOrderManualTalentSearchFilters, { requestId }) =>
    await longOrderManualTalentSearchQuery(filters, { requestId }),
);

function moveCandidatesToState(
  state: SliceState,
  events: { payload: CandidateRejected }[],
  status: CandidateStatus,
  currentUser: User | null,
): Candidate[] {
  const candidatesToMove: Candidate[] = [];

  events.forEach((event) => {
    const talentId = event.payload.talentId;

    // find candidate from recommendedCandidates and jobAdApplicantCandidates
    const recommendedCandidate = selectRecommendedById(state, talentId);
    const jobAdApplicantCandidate = selectJobAdApplicantById(state, talentId);
    const pickedCandidate = selectPickedById(state, talentId);

    // Snapshot fields that exist only in jobAdApplicantCandidate (e.g. exclusivityEntryIds) before
    // removeOne mutates the Immer draft. A talent can be simultaneously in both recommended and
    // jobAdApplicants stores; candidateToPick below picks recommended first, which loses jobAd-only
    // fields
    const jobAdCandidateSnapshot = jobAdApplicantCandidate
      ? (current(jobAdApplicantCandidate) as JobAdApplicant)
      : undefined;

    // remove candidate from previous states
    if (recommendedCandidate) {
      talentsStatusAdapter.removeOne(state.recommended, talentId);
    }
    if (jobAdApplicantCandidate) {
      talentStatusJobAdApplicantsAdapter.removeOne(
        state.jobAdApplicants,
        talentId,
      );
    }

    // modify candidate to type and add to candidatesToMove
    const candidateToPick: Candidate | JobAdApplicant | PickedCandidate =
      recommendedCandidate || jobAdApplicantCandidate || pickedCandidate;

    if (candidateToPick) {
      const currentCandidate = current(candidateToPick) as
        | PickedCandidate
        | Candidate
        | JobAdApplicant;

      const formattedCandidate: Candidate = {
        ...currentCandidate,
        talentId:
          "talentId" in currentCandidate
            ? currentCandidate.talentId
            : currentCandidate.id,
        [status]: {
          ...(status === CandidateStatus.EezyRejected && {
            rejectionReason: event.payload.rejectionReason,
            rejectionComment: event.payload.rejectionComment,
          }),
          userId: currentUser?.id ?? nanoid(),
          firstName: currentUser?.firstName ?? "Unknown",
          lastName: currentUser?.lastName ?? "Unknown",
          at: new Date().getTime(),
        },
        ...(jobAdCandidateSnapshot && {
          applieadAt: jobAdCandidateSnapshot.appliedAt,
          hasCoverLetter: jobAdCandidateSnapshot.hasCoverLetter,
          exclusivityEntryIds: jobAdCandidateSnapshot.exclusivityEntryIds,
        }),
      };

      if (
        "rejectionMessageSentAt" in currentCandidate &&
        currentCandidate.rejectionMessageSentAt
      ) {
        formattedCandidate.rejectionMessageSent = {
          userId: nanoid(),
          firstName: "Unknown",
          lastName: "Unknown",
          at: currentCandidate.rejectionMessageSentAt,
        };
      }

      candidatesToMove.push(formattedCandidate);
    }
  });

  return candidatesToMove;
}

function upsertPickedCandidate(
  state: SliceState,
  candidate: Candidate,
  currentUser: User | null,
) {
  const candidateWithPicked = candidate.picked
    ? candidate
    : {
        ...candidate,
        [CandidateStatus.Picked]: {
          userId: currentUser?.id ?? nanoid(),
          firstName: currentUser?.firstName ?? "Unknown",
          lastName: currentUser?.lastName ?? "Unknown",
          at: new Date().getTime(),
        },
      };

  if (selectPickedById(state, candidateWithPicked.talentId)) {
    talentsStatusAdapter.updateOne(state.picked, {
      id: candidateWithPicked.talentId,
      changes: { ...candidateWithPicked },
    });
    return;
  }

  talentsStatusAdapter.addOne(state.picked, candidateWithPicked);
}

export const talentsStatusSlice = createSlice({
  name: "talentsStatus",
  initialState: {
    picked: talentsStatusAdapter.getInitialState({}),
    recommended: talentsStatusAdapter.getInitialState({}),
    jobAdApplicants: talentStatusJobAdApplicantsAdapter.getInitialState({}),
    status: "idle",
  } as SliceState,
  reducers: {
    clearTalentsStatus: (state) => {
      talentsStatusAdapter.removeAll(state.picked);
      talentsStatusAdapter.removeAll(state.recommended);
      talentStatusJobAdApplicantsAdapter.removeAll(state.jobAdApplicants);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(pickCandidates.pending, (state) => {
        state.status = "pending";
      })
      .addCase(pickCandidates.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.Picked,
          action.payload.currentUser,
        );

        // other talents from LongOrderManualTalentSearch query
        const additionalCandidates: Candidate[] = [];
        if ("talents" in action.meta.arg) {
          action.meta.arg.talents.forEach((talent) => {
            const isCandidateProcessed =
              state.recommended.entities[talent.talentId] ||
              state.jobAdApplicants.entities[talent.talentId];

            if (!isCandidateProcessed) {
              additionalCandidates.push({
                ...talent,
                [CandidateStatus.Picked]: {
                  userId: action.payload.currentUser?.id ?? nanoid(),
                  firstName: action.payload.currentUser?.firstName ?? "Unknown",
                  lastName: action.payload.currentUser?.lastName ?? "Unknown",
                  at: new Date().getTime(),
                },
              });
            }
          });
        }

        const allCandidatesToMove = [
          ...candidatesToMove,
          ...additionalCandidates,
        ];

        // add to picked state
        talentsStatusAdapter.addMany(state.picked, allCandidatesToMove);
        state.status = "succeeded";
      })
      .addCase(introduceToCustomer.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(introduceToCustomer.pending, (state) => {
        state.status = "pending";
      })
      .addCase(introduceToCustomer.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.Introduced,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            candidate,
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(chooseForInterview.pending, (state) => {
        state.status = "pending";
      })
      .addCase(chooseForInterview.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.ChosenForInterview,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            candidate,
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(chooseForInterview.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(approveIntroducedTalents.pending, (state) => {
        state.status = "pending";
      })
      .addCase(approveIntroducedTalents.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.CustomerApproved,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            {
              ...candidate,
              customerApproved: candidate.customerApproved
                ? {
                    ...candidate.customerApproved,
                    success: action.meta.arg.approved,
                    rejectionReason: action.meta.arg.approved ? null : action.meta.arg.rejectionReason,
                    rejectionComment: action.meta.arg.approved ? null : action.meta.arg.rejectionComment,
                  }
                : candidate.customerApproved,
            },
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(approveIntroducedTalents.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(withdrawTalent.pending, (state) => {
        state.status = "pending";
      })
      .addCase(withdrawTalent.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.Withdrew,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            candidate,
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(withdrawTalent.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(cancelTalentWithdrawal.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelTalentWithdrawal.fulfilled, (state, action) => {
        action.payload.forEach((event) => {
          if (event.name === "cndWithdrewDel") {
            const currentCandidate = selectPickedById(state, event.payload.talentId);
            if (currentCandidate) {
              talentsStatusAdapter.updateOne(state.picked, {
                id: event.payload.talentId,
                changes: {
                  ...current(currentCandidate),
                  withdrew: undefined,
                },
              });
            }
          }
        });

        state.status = "succeeded";
      })
      .addCase(cancelTalentWithdrawal.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(cancelIntroduceToCustomer.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(cancelIntroduceToCustomer.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelIntroduceToCustomer.fulfilled, (state, action) => {
        action.payload.forEach((event) => {
          if (event.name === "cndIntrToCustDel") {
            event.payload.talentIds.forEach((talentId) => {
              const introducedCandidate = selectPickedById(state, talentId);

              const currentIntroducedCandidate = current(introducedCandidate);

              talentsStatusAdapter.updateOne(state.picked, {
                id: talentId,
                changes: {
                  ...currentIntroducedCandidate,
                  introduced: undefined, // remove introduced status
                },
              });
            });
          }
        });

        state.status = "succeeded";
      })
      .addCase(rejectCandidates.pending, (state) => {
        state.status = "pending";
      })
      .addCase(rejectCandidates.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data.filter(event => event.name === "cndRjctdEezy"),
          CandidateStatus.EezyRejected,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            candidate,
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(rejectCandidates.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(cancelTalentRejection.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelTalentRejection.fulfilled, (state, action) => {
        action.payload.forEach((event) => {
          if (event.name === "cndRjctdEezyDel") {
            const currentCandidate = selectPickedById(state, event.payload.talentId);
            if (currentCandidate) {
              talentsStatusAdapter.updateOne(state.picked, {
                id: event.payload.talentId,
                changes: {
                  ...current(currentCandidate),
                  eezyRejected: undefined,
                },
              });
            }
          }
        });
        state.status = "succeeded";
      })
      .addCase(cancelTalentRejection.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(sendRejectedMessageToCandidates.pending, (state) => {
        state.status = "pending";
      })
      .addCase(sendRejectedMessageToCandidates.fulfilled, (state, action) => {
        action.payload.forEach((event) => {
          if (event.name === "cndRjctnMsg") {
            const currentCandidate = selectPickedById(state, event.payload.talentId);
            if (currentCandidate) {
              talentsStatusAdapter.updateOne(state.picked, {
                id: event.payload.talentId,
                changes: {
                  ...current(currentCandidate),
                  rejectionMessageSent: {
                    userId: nanoid(),
                    firstName: "Unknown",
                    lastName: "Unknown",
                    at: new Date().getTime(),
                  },
                },
              });
            }
          }
        });
        state.status = "succeeded";
      })
      .addCase(sendRejectedMessageToCandidates.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(askWorkInterest.pending, (state) => {
        state.status = "pending";
      })
      .addCase(askWorkInterest.fulfilled, (state, action) => {
        const candidatesToMove = moveCandidatesToState(
          state,
          action.payload.data,
          CandidateStatus.InterestAsked,
          action.payload.currentUser,
        );

        candidatesToMove.forEach((candidate) => {
          upsertPickedCandidate(
            state,
            candidate,
            action.payload.currentUser,
          );
        });
        state.status = "succeeded";
      })
      .addCase(askWorkInterest.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(pickCandidates.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchCandidates.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        talentsStatusAdapter.setAll(
          state.recommended,
          action.payload.recommended,
        );

        talentsStatusAdapter.setAll(state.picked, action.payload.picked);

        talentStatusJobAdApplicantsAdapter.setAll(
          state.jobAdApplicants,
          action.payload.applicants,
        );

        state.status = "succeeded";
      })
      .addCase(fetchCandidates.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchOtherTalents.pending, (state) => {
        state.status = "pending";
      })
      .addCase(searchOtherTalents.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(searchOtherTalents.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { selectAll: selectAllPickedCandidates } =
  talentsStatusAdapter.getSelectors(
    (state: RootState) => state.talentsStatus.picked,
  );
export const { selectAll: selectAllRecommendedCandidates } =
  talentsStatusAdapter.getSelectors(
    (state: RootState) => state.talentsStatus.recommended,
  );

export const { selectAll: selectAllTalentStatusJobAdApplicants } =
  talentStatusJobAdApplicantsAdapter.getSelectors(
    (state: RootState) => state.talentsStatus.jobAdApplicants,
  );

export const { clearTalentsStatus } = talentsStatusSlice.actions;

export const talentsStatusReducer = talentsStatusSlice.reducer;
